import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { checkNetSend, checkSandboxMsg, PROTOCOL_VERSION, wheelUnits, type HostToRunnerNet, type HostToSandbox, type LoadPayload, type PadState } from '../../shared/protocol.ts';
import { netModel } from '../../shared/manifest.ts';
import type { BootInfo, NetSession, NetSink } from '../net/session.ts';
import { OnlinePanel, useNet } from './online.tsx';
import { sandboxOrigin } from '../../shared/sandbox-origin.ts';
import type { GameFiles, OpenedGame } from '../lib/game.ts';
import {
  gamepadState, KeyboardPad, loadKeymap, mergePads, NO_INPUT, resetKeymap, saveKeymap, type GamepadLike, type Keymap,
} from '../lib/keyboard.ts';
import { loadPref, savePref } from '../lib/prefs.ts';
import { RtwError } from '../lib/errors.ts';
import { ErrorBox } from '../components.tsx';
import { TouchControls, touchLikely, type TouchMode } from './touch.tsx';
import { FileInputsSection, FilesSection, KeysSection, Menu, OptionsSection, SaveSection, SoundSection, TouchSection } from './menu.tsx';
import { AudioOut } from '../audio/engine.ts';
import { ensurePersisted, FILES_CAP, safeFileName, safeMime, type GameFile } from '../lib/saves.ts';
import { addFile, deleteFile, idbAvailable, listFiles } from '../lib/db.ts';

// allow-same-origin gives the frame its real g-<hash> origin (own storage and service worker). That origin is
// still cross-origin to the host, and game code only ever runs in the CSP-locked blob: worker inside it.
export const IFRAME_SANDBOX = 'allow-scripts allow-same-origin allow-pointer-lock';
export const IFRAME_ALLOW = 'autoplay; fullscreen; gamepad';

/** Picture aspect (width / height) the layout reserves per platform. The game's own resolution never changes it. */
const ASPECT: Record<string, number> = { gba: 3 / 2, n64: 4 / 3 };

export interface PlayerProps {
  game: OpenedGame;
  files: GameFiles;
  rom: Uint8Array;
  save?: Uint8Array;
  /** Store the native save (rtw_save_generation changed). */
  onSave?: (bytes: Uint8Array, generation: number) => Promise<void>;
  /** The player imported a save file: store it and restart the game with it. */
  onImportSave?: (bytes: Uint8Array) => void;
  onExit: () => void;
  /** v1.2 online play: the game page's session (games with net.players > 1). */
  net?: NetSession;
  /** v1.2: this run is a lockstep session boot (the room host's options and save). */
  boot?: BootInfo;
  /** v1.2: a room code from an invite link: open the Online panel and join once the game runs. */
  invite?: string | null;
  onInviteUsed?: () => void;
  /** v1.2: the room host's current options and save, read by the session when it starts. */
  localRef?: { current: (() => { options: [number, number][]; save?: Uint8Array }) | null };
}

function copy(b: Uint8Array): ArrayBuffer {
  return b.slice().buffer as ArrayBuffer;
}

const optionsPref = (id: string) => `rtw:options:${id}`;

type Vibrator = { playEffect?(t: string, p: Record<string, number>): Promise<unknown>; reset?(): Promise<unknown> };

function padsNow(): (GamepadLike & { vibrationActuator?: Vibrator } | null)[] {
  try { return [...(navigator.getGamepads?.() ?? [])] as (GamepadLike & { vibrationActuator?: Vibrator } | null)[]; } catch { return []; }
}

export function Player({ game, files, rom, save, onSave, onImportSave, onExit, net, boot, invite, onInviteUsed, localRef }: PlayerProps) {
  const m = game.manifest;
  useNet(net);
  const online = !!net && !!netModel(m);
  const inSession = online && !!net?.inSession();
  const sessionRef = useRef(false);
  sessionRef.current = inSession;
  const [onlineOpen, setOnlineOpen] = useState(false);
  const [selfHiddenNote, setSelfHiddenNote] = useState(false);
  const extra = useMemo(() => m.controls?.extra ?? [], [m]);
  const frame = useRef<HTMLIFrameElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const mouseEl = useRef<HTMLDivElement>(null);
  const [origin, setOrigin] = useState<string | null>(null);
  // The iframe is created only once our message listener is in place, so the loader's hello can't be missed.
  const [frameSrc, setFrameSrc] = useState<string | null>(null);
  const [status, setStatus] = useState('Starting…');
  const [error, setError] = useState<RtwError | null>(null);
  const [userPaused, setUserPaused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [touchMode, setTouchModeState] = useState<TouchMode>(() => loadPref<TouchMode>('rtw:touch', 'auto'));
  const [touchSeen, setTouchSeen] = useState(() => touchLikely());
  const [keymap, setKeymapState] = useState<Keymap>(() => loadKeymap(m.platform, extra));
  const [options, setOptions] = useState<Record<number, number>>(() => loadPref<Record<number, number>>(optionsPref(m.id), {}));
  const [wantMouse, setWantMouse] = useState(false);
  const [locked, setLocked] = useState(false);
  const [fileAsk, setFileAsk] = useState<number | null>(null); // v1.1: the game asked for fileInputs[id]
  const [fileMsg, setFileMsg] = useState('');
  const portRef = useRef<MessagePort | null>(null);
  const logs = useRef<string[]>([]);
  const pausedRef = useRef({ user: false, menu: false });
  const kbRef = useRef(new KeyboardPad(m.platform, extra, keymap));
  const touchRef = useRef<PadState>(NO_INPUT);
  const captureRef = useRef<((code: string) => void) | null>(null);
  const updateRef = useRef<() => void>(() => {});
  const pauseRef = useRef<() => void>(() => {});
  const audio = useMemo(() => new AudioOut(), []);
  const latestSave = useRef<Uint8Array | undefined>(save);
  const [saveInfo, setSaveInfo] = useState<{ at: number; error?: string } | null>(null);
  const [persistWarn, setPersistWarn] = useState(false);
  const [gameFiles, setGameFiles] = useState<GameFile[]>([]);
  const [unseen, setUnseen] = useState(0);
  const [filesNote, setFilesNote] = useState('');
  const persistAsked = useRef(false);
  const [, setAudioTick] = useState(0);
  const sendRef = useRef<(msg: HostToSandbox, transfer?: Transferable[]) => void>(() => {});
  const netCleanup = useRef<(() => void) | null>(null);
  const inviteRef = useRef(invite ?? null);

  // the menu's option values and their stored copy (sent to rtw_option at the next start)
  const storeOption = (id: number, value: number) => {
    const next = { ...loadPref<Record<number, number>>(optionsPref(m.id), {}), [id]: value };
    savePref(optionsPref(m.id), next);
    setOptions((cur) => ({ ...cur, [id]: value }));
  };

  const showTouch = touchMode === 'on' || (touchMode === 'auto' && touchSeen);
  if (localRef) {
    localRef.current = () => {
      const stored = loadPref<Record<number, number>>(optionsPref(m.id), {});
      return {
        options: (m.options ?? []).map((o) => [o.id, typeof stored[o.id] === 'number' ? stored[o.id] : o.default ?? 0] as [number, number]),
        save: latestSave.current,
      };
    };
  }

  useEffect(() => { sandboxOrigin(location, game.link.owner, game.link.repo).then(setOrigin); }, []);

  // Saved files (rtw_export_file) live on this device per game id; without IndexedDB they last for the session.
  const filesStored = useMemo(() => idbAvailable(), []);
  const refreshFiles = () => listFiles(m.id).then((l) => setGameFiles(l), () => {});
  useEffect(() => { if (filesStored) refreshFiles(); }, []);
  const keepFile = (f: GameFile) => {
    setUnseen((n) => n + 1);
    if (!filesStored) { setGameFiles((l) => [...l.slice(-49), f]); return; }
    addFile({ game: m.id, name: f.name, mime: f.mime, bytes: f.bytes, at: f.at }, FILES_CAP).then(
      ({ dropped }) => {
        if (dropped.length) setFilesNote(`Removed the oldest saved file${dropped.length > 1 ? 's' : ''} (${dropped.join(', ')}) to make room.`);
        return refreshFiles();
      },
      (err: unknown) => { setFilesNote(`Could not keep ${f.name} on this device (${String(err)}); it stays until you close the game.`); setGameFiles((l) => [...l, f]); },
    );
  };
  const removeFile = (id: number) => {
    setGameFiles((l) => l.filter((f) => f.id !== id));
    if (filesStored) deleteFile(id).catch(() => {});
  };

  useEffect(() => {
    if (!origin) return;
    const kb = kbRef.current;
    let lastPads = '';
    let gpPads: PadState[] = [];
    const send = (msg: HostToSandbox, transfer: Transferable[] = []) => portRef.current?.postMessage(msg, transfer);
    // audio starts on the first gesture (ours, or one the sandbox relays); its state is mirrored for tests
    const gesture = () => { audio.start(); };
    let audioAttr = 0;
    audio.onChange = () => {
      const now = performance.now();
      if (now - audioAttr > 200) { audioAttr = now; root.current?.setAttribute('data-audio', JSON.stringify(audio.state())); }
      setAudioTick((n) => n + 1);
    };
    sendRef.current = send;
    const sendPads = () => {
      const pads = [mergePads(kb.state(), touchRef.current, gpPads[0] ?? NO_INPUT), ...gpPads.slice(1, 4).map((p) => p ?? NO_INPUT)];
      const k = JSON.stringify(pads);
      if (k !== lastPads) { lastPads = k; send({ t: 'input', pads }); }
    };
    updateRef.current = sendPads;
    const onKey = (e: KeyboardEvent | { code: string; type: string; repeat?: boolean; target?: EventTarget | null; preventDefault?: () => void }) => {
      const t = e.target as HTMLElement | null;
      if (captureRef.current) {
        if (e.type === 'keydown' && !e.repeat) { e.preventDefault?.(); captureRef.current(e.code); }
        return;
      }
      if (e.repeat || t?.tagName === 'INPUT' || t?.tagName === 'SELECT') return;
      if (kb.set(e.code, e.type === 'keydown')) { e.preventDefault?.(); sendPads(); }
    };
    const pauseState = () => {
      const p = pausedRef.current;
      // v1.2: never pause in an online session (a paused peer stalls everyone)
      const on = !sessionRef.current && (document.visibilityState === 'hidden' || p.user || p.menu);
      send({ t: 'pause', on });
      audio.pause(on);
      if (sessionRef.current) net?.setHidden(document.visibilityState === 'hidden');
    };
    pauseRef.current = pauseState;
    let wake: { release(): Promise<void> } | null = null;
    const keepAwake = () => {
      const wl = (navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<{ release(): Promise<void> }> } }).wakeLock;
      if (wl && document.visibilityState === 'visible') wl.request('screen').then((l) => { wake = l; }, () => {});
    };
    const onVis = () => {
      kb.clear(); sendPads(); pauseState();
      if (document.visibilityState === 'visible') keepAwake();
      if (sessionRef.current && document.visibilityState === 'hidden') setSelfHiddenNote(true);
    };
    const onBlur = () => { kb.clear(); sendPads(); };
    const onTouch = () => setTouchSeen(true);

    // rumble: navigator.vibrate and the gamepad's actuator, refreshed while the game keeps it on
    let rumbleTimer = 0;
    const rumble = (on: boolean) => {
      clearInterval(rumbleTimer);
      const pulse = () => {
        try { navigator.vibrate?.(on ? 400 : 0); } catch { /* unsupported */ }
        for (const gp of padsNow()) {
          const a = gp?.vibrationActuator;
          if (!a) continue;
          if (on) a.playEffect?.('dual-rumble', { duration: 400, strongMagnitude: 0.8, weakMagnitude: 0.6 })?.catch(() => {});
          else a.reset?.()?.catch(() => {});
        }
      };
      pulse();
      if (on) rumbleTimer = setInterval(pulse, 350) as unknown as number;
    };

    // gamepad polling + mouse flush, once per display frame
    let raf = 0;
    let mdx = 0, mdy = 0, mbuttons = 0, lastMb = 0, wheel = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const gps = padsNow();
      const next: PadState[] = [];
      for (const gp of gps) if (gp && gp.mapping === 'standard') next.push(gamepadState(gp, m.platform, extra));
      if (next.length || gpPads.length) { gpPads = next; sendPads(); }
      if (mdx || mdy || mbuttons !== lastMb) {
        send({ t: 'mouse', dx: Math.max(-32768, Math.min(32767, Math.round(mdx))), dy: Math.max(-32768, Math.min(32767, Math.round(mdy))), buttons: mbuttons });
        mdx = 0; mdy = 0; lastMb = mbuttons;
      }
      if (wheel) { send({ t: 'wheel', dy: Math.max(-32768, Math.min(32767, wheel)) }); wheel = 0; }
    };
    raf = requestAnimationFrame(loop);
    const isLocked = () => !!mouseEl.current && document.pointerLockElement === mouseEl.current;
    const onMouseMove = (e: MouseEvent) => { if (isLocked()) { mdx += e.movementX; mdy += e.movementY; } };
    const onMouseBtn = (e: MouseEvent) => {
      if (!isLocked()) return;
      const bit = e.button === 0 ? 1 : e.button === 2 ? 2 : e.button === 1 ? 4 : 0;
      mbuttons = e.type === 'mousedown' ? mbuttons | bit : mbuttons & ~bit;
    };
    // while the pointer is captured the wheel lands here (over the picture otherwise it reaches the sandbox itself)
    const onWheel = (e: WheelEvent) => { if (isLocked()) { e.preventDefault(); wheel += wheelUnits(e); } };
    const onLockChange = () => { const l = isLocked(); setLocked(l); if (!l) mbuttons = 0; };

    const onWindowMsg = (e: MessageEvent) => {
      // Only our own iframe, on the repo's sandbox origin.
      if (e.source !== frame.current?.contentWindow || e.origin !== origin) return;
      const d = e.data as { rtw?: string; v?: number };
      if (d?.rtw !== 'hello' || d.v !== PROTOCOL_VERSION || portRef.current) return;
      const ch = new MessageChannel();
      portRef.current = ch.port1;
      ch.port1.onmessage = (ev) => {
        const msg = checkSandboxMsg(ev.data, m);
        if (!msg) return;
        switch (msg.t) {
          case 'ready': {
            const stored = loadPref<Record<number, number>>(optionsPref(m.id), {});
            const payload: LoadPayload = {
              manifest: { id: m.id, platform: m.platform, loop: m.module.loop, video: m.video, hz: m.hz ?? 60, factory: m.module.factory, memory: m.memory },
              buildId: game.buildId,
              wasm: copy(files.wasm),
              ...(files.js ? { js: copy(files.js) } : {}),
              data: Object.fromEntries(Object.entries(files.data).map(([k, v]) => [k, copy(v)])),
              rom: copy(rom),
              ...(save ? { save: copy(save) } : {}),
              // a lockstep session boot uses the room host's option values (and save, passed in as `save`)
              options: boot?.model === 'lockstep' ? boot.options
                : (m.options ?? []).map((o) => [o.id, typeof stored[o.id] === 'number' ? stored[o.id] : o.default ?? 0] as [number, number]),
              ...(boot?.model === 'lockstep' ? { net: boot.start } : {}),
            };
            const transfer: Transferable[] = [payload.wasm, payload.rom, ...(payload.js ? [payload.js] : []), ...(payload.save ? [payload.save] : []), ...Object.values(payload.data)];
            // v1.2: the net port goes straight to the game's worker; packets never pass the loader's main thread
            let netPort: MessagePort | undefined;
            if (online && net) {
              const ch2 = new MessageChannel();
              netPort = ch2.port2;
              transfer.push(ch2.port2);
              const toGame = (msg: HostToRunnerNet, t: Transferable[] = []) => ch2.port1.postMessage(msg, t);
              ch2.port1.onmessage = (ev) => {
                const out = checkNetSend(ev.data);
                if (out) net.sendGame(out.peer, out.ch, new Uint8Array(out.bytes));
              };
              const sink: NetSink = {
                packet: (peer, chn, bytes) => toGame({ t: 'recv', peer, ch: chn, bytes }, [bytes]),
                peer: (peer, state) => toGame({ t: 'peer', peer, state }),
                rtt: (rtt) => toGame({ t: 'rtt', rtt }),
                session: (start) => { toGame({ t: 'session', ...start }); send({ t: 'net-mode', on: true }); pauseRef.current(); },
              };
              netCleanup.current = () => { net.detach(sink); ch2.port1.close(); };
              send({ t: 'load', ...payload, netPort }, transfer);
              net.attach(sink);
            } else {
              send({ t: 'load', ...payload }, transfer);
            }
            break;
          }
          case 'started':
            setStatus(`${msg.width}×${msg.height}`); lastPads = ''; sendPads(); pauseState(); keepAwake();
            // v1.2: an invite link joins its room once the game runs
            if (inviteRef.current && net) {
              const code = inviteRef.current;
              inviteRef.current = null;
              onInviteUsed?.();
              setOnlineOpen(true);
              if (net.phase === 'idle') net.join(code);
            }
            break;
          case 'stats':
            root.current?.setAttribute('data-frames', String(msg.frames));
            root.current?.setAttribute('data-res', `${msg.width}x${msg.height}`);
            setStatus(`${msg.width}×${msg.height} · ${Math.round(msg.fps)} fps`);
            break;
          case 'log': logs.current = [...logs.current.slice(-199), msg.text]; root.current?.setAttribute('data-log', logs.current.join('\n')); break;
          case 'key': if (msg.down) gesture(); onKey({ code: msg.code, type: msg.down ? 'keydown' : 'keyup' }); break;
          case 'gesture': gesture(); break;
          case 'audio': audio.feed(msg.rate, msg.samples); break;
          case 'save': {
            const bytes = new Uint8Array(msg.bytes);
            latestSave.current = bytes;
            (onSave?.(bytes, msg.generation) ?? Promise.resolve()).then(
              () => setSaveInfo({ at: Date.now() }),
              (err: unknown) => setSaveInfo({ at: Date.now(), error: String(err) }),
            );
            if (!persistAsked.current) {
              persistAsked.current = true;
              ensurePersisted().then((ok) => { if (ok === false) setPersistWarn(true); });
            }
            break;
          }
          case 'file':
            keepFile({ id: Date.now() + Math.random(), name: safeFileName(msg.name), mime: safeMime(msg.mime), bytes: new Uint8Array(msg.bytes), at: Date.now() });
            break;
          case 'rumble': if (msg.port === 0) rumble(msg.on); break;
          case 'request-file': if (m.fileInputs?.some((i) => i.id === msg.id)) setFileAsk(msg.id); break;
          // v1.1: the game changed an option from its own menu: show and keep it, never echo it back as rtw_option
          case 'option-changed': storeOption(msg.id, msg.value); break;
          case 'pointer-lock':
            setWantMouse(msg.on);
            if (!msg.on && isLocked()) document.exitPointerLock?.();
            break;
          case 'error':
            // the runner names feature-check failures "Unsupported": its message is already the plain explanation
            setError(msg.detail.startsWith('Unsupported:')
              ? new RtwError('unsupported', msg.message, msg.detail)
              : new RtwError('crash', 'The game stopped with an error.', `${msg.message}\n${msg.detail}`));
            break;
          case 'probe-result': root.current?.setAttribute('data-probe', JSON.stringify(msg.results)); break;
        }
      };
      (e.source as Window).postMessage({ rtw: 'connect', v: PROTOCOL_VERSION }, origin, [ch.port2]);
    };

    // test hook for the e2e suite: ask the loader to try the network
    const onProbe = (e: Event) => send({ t: 'probe-network', urls: (e as CustomEvent<string[]>).detail });
    const r = root.current;
    r?.addEventListener('rtw-probe', onProbe);
    addEventListener('message', onWindowMsg);
    for (const t of ['pointerdown', 'keydown', 'touchend'] as const) addEventListener(t, gesture, { capture: true, passive: true });
    addEventListener('keydown', onKey);
    addEventListener('keyup', onKey);
    addEventListener('blur', onBlur);
    addEventListener('touchstart', onTouch, { passive: true, capture: true });
    addEventListener('mousemove', onMouseMove);
    addEventListener('mousedown', onMouseBtn);
    addEventListener('mouseup', onMouseBtn);
    addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('pointerlockchange', onLockChange);
    document.addEventListener('visibilitychange', onVis);
    setFrameSrc(origin + '/');
    return () => {
      cancelAnimationFrame(raf);
      for (const t of ['pointerdown', 'keydown', 'touchend'] as const) removeEventListener(t, gesture, { capture: true });
      audio.close();
      rumble(false);
      removeEventListener('message', onWindowMsg);
      removeEventListener('keydown', onKey);
      removeEventListener('keyup', onKey);
      removeEventListener('blur', onBlur);
      removeEventListener('touchstart', onTouch, { capture: true });
      removeEventListener('mousemove', onMouseMove);
      removeEventListener('mousedown', onMouseBtn);
      removeEventListener('mouseup', onMouseBtn);
      removeEventListener('wheel', onWheel);
      document.removeEventListener('pointerlockchange', onLockChange);
      document.removeEventListener('visibilitychange', onVis);
      r?.removeEventListener('rtw-probe', onProbe);
      if (isLocked()) document.exitPointerLock?.();
      portRef.current?.close();
      portRef.current = null;
      netCleanup.current?.();
      netCleanup.current = null;
      wake?.release().catch(() => {});
    };
  }, [origin]);

  // a session never pauses: re-apply the pause state when one begins or ends
  useEffect(() => { pauseRef.current(); }, [inSession]);
  const togglePause = () => {
    pausedRef.current.user = !pausedRef.current.user;
    setUserPaused(pausedRef.current.user);
    pauseRef.current();
  };
  const setMenu = (open: boolean) => {
    pausedRef.current.menu = open;
    setMenuOpen(open);
    if (open) { kbRef.current.clear(); updateRef.current(); }
    pauseRef.current();
  };
  const fullscreen = () => { root.current?.requestFullscreen?.().catch(() => {}); };
  const setTouchMode = (t: TouchMode) => { setTouchModeState(t); savePref('rtw:touch', t); };
  const setKeymap = (k: Keymap) => { setKeymapState(k); kbRef.current.map = k; saveKeymap(m.platform, k); };
  const resetKeys = () => { resetKeymap(m.platform); const k = loadKeymap(m.platform, extra); setKeymapState(k); kbRef.current.map = k; };
  const setOption = (id: number, value: number) => {
    storeOption(id, value);
    sendRef.current({ t: 'option', id, value });
  };
  // v1.1: hand a file to the game (fileInputs); the size limit is checked here, the bytes are copied, never shared
  const giveFile = (id: number, bytes: Uint8Array, name: string) => {
    const input = m.fileInputs?.find((i) => i.id === id);
    if (!input) return;
    const max = input.maxSize ?? 64 << 20;
    if (bytes.length > max) { setFileMsg(`${name} is larger than this game accepts (${max} bytes).`); return; }
    const buf = copy(bytes);
    sendRef.current({ t: 'give-file', id, bytes: buf }, [buf]);
    setFileMsg(''); setFileAsk(null);
    if (menuOpen) setMenu(false);
  };
  const pickFile = (input: { id: number }, f: File) => { f.arrayBuffer().then((b) => giveFile(input.id, new Uint8Array(b), f.name), () => {}); };
  const askInput = m.fileInputs?.find((i) => i.id === fileAsk);
  const onTouchPad = useRef((s: PadState) => { touchRef.current = s; updateRef.current(); }).current;

  return (
    <div class={'play' + (showTouch ? ' touch' : '')} ref={root} data-state="playing" data-platform={m.platform}
      style={{ '--aspect': String(ASPECT[m.platform] ?? 4 / 3) }}>
      <div class="bar">
        <button onClick={onExit} aria-label="Exit">✕</button>
        {!inSession && <button onClick={togglePause} data-action="pause" aria-label="Pause">{userPaused ? '▶' : '❚❚'}</button>}
        <button onClick={() => setMenu(!menuOpen)} data-action="menu" aria-label="Menu">☰</button>
        <button onClick={() => audio.setMuted(!audio.prefs.muted)} data-action="mute" aria-label={audio.prefs.muted ? 'Unmute' : 'Mute'}>
          {audio.prefs.muted ? '🔇' : '🔊'}
        </button>
        <button onClick={fullscreen} aria-label="Fullscreen">⛶</button>
        {online && (
          <button data-action="online" class={inSession ? 'net-on' : ''} onClick={() => setOnlineOpen(!onlineOpen)} aria-label="Online">
            🌐{inSession ? ` ${net!.boot?.ids.length ?? ''}P` : net!.phase === 'lobby' ? ' …' : ''}
          </button>
        )}
        {gameFiles.length > 0 && (
          <button data-action="files" onClick={() => { setUnseen(0); setMenu(true); }} aria-label="Saved files">
            📄{unseen > 0 ? ` ${unseen}` : ''}
          </button>
        )}
        <span class="status" data-field="status">{status}</span>
      </div>
      {error && <ErrorBox error={error} />}
      {persistWarn && (
        <div class="warning toast" role="alert" data-state="persist-warning">
          This browser may delete saved games when it runs low on space. Use ☰ → Export save to keep a copy.
          {' '}<button onClick={() => setPersistWarn(false)}>OK</button>
        </div>
      )}
      {askInput && (
        <div class="banner toast" role="dialog" data-state="file-request">
          The game asks for a file: {askInput.label}{' '}
          <label class="button-like">Choose…
            <input type="file" data-field="file-request-input" accept={askInput.accept.join(',')}
              onChange={(e) => { const el = e.target as HTMLInputElement; const f = el.files?.[0]; el.value = ''; if (f) pickFile(askInput, f); }} />
          </label>{' '}
          <button onClick={() => setFileAsk(null)}>Cancel</button>
        </div>
      )}
      {fileMsg && <div class="warning toast" role="alert">{fileMsg} <button onClick={() => setFileMsg('')}>OK</button></div>}
      {inSession && net!.hiddenPlayers().length > 0 && (
        <div class="warning toast" role="alert" data-state="net-hidden-warning">
          Player {net!.hiddenPlayers().join(', ')} switched to another tab: the game may stall until they come back.
        </div>
      )}
      {inSession && selfHiddenNote && (
        <div class="warning toast" role="alert" data-state="net-self-hidden">
          You switched away from this tab during an online game: that can stall the game for everyone. Keep it in front.
          {' '}<button onClick={() => setSelfHiddenNote(false)}>OK</button>
        </div>
      )}
      <div class="screen" data-field="screen">
        {frameSrc && (
          <iframe ref={frame} src={frameSrc} title={m.title}
            sandbox={IFRAME_SANDBOX} allow={IFRAME_ALLOW} referrerpolicy="no-referrer" />
        )}
        {wantMouse && (
          <div class={'mouse-catch' + (locked ? ' locked' : '')} ref={mouseEl} data-field="mouse-catch"
            data-locked={locked ? '1' : '0'} onClick={() => { if (!locked) mouseEl.current?.requestPointerLock?.(); }}
            onContextMenu={(e) => e.preventDefault()}>
            {!locked && <span>Click to use the mouse (Esc gives it back)</span>}
          </div>
        )}
      </div>
      {showTouch && !menuOpen && !onlineOpen && <TouchControls platform={m.platform} extra={extra} onChange={onTouchPad} />}
      {onlineOpen && net && <OnlinePanel net={net} max={m.net?.players ?? 2} onClose={() => setOnlineOpen(false)} />}
      {menuOpen && (
        <Menu onClose={() => setMenu(false)}>
          <FileInputsSection inputs={m.fileInputs} onPick={pickFile} />
          <FilesSection files={gameFiles} stored={filesStored} note={filesNote} onRemove={removeFile}
            inputs={m.fileInputs} onOpen={(i, f) => giveFile(i.id, f.bytes, f.name)} />
          <SaveSection manifest={m} current={() => latestSave.current} info={saveInfo} onImport={(b) => onImportSave?.(b)} />
          <SoundSection audio={audio} />
          <OptionsSection manifest={m} values={options} onValue={setOption} />
          <TouchSection mode={touchMode} onMode={setTouchMode} />
          <KeysSection manifest={m} map={keymap} onMap={setKeymap} onReset={resetKeys}
            capture={(cb) => { captureRef.current = cb; }} />
        </Menu>
      )}
    </div>
  );
}
